import React, { useState } from 'react';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { blogPosts } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Get unique categories
  const allCategories = ['All', ...new Set(blogPosts.map(post => post.category))];

  // Filter posts based on category and search query
  const filteredPosts = blogPosts.filter(post => {
    // Category filter
    const categoryMatch = selectedCategory === 'All' || post.category === selectedCategory;
    
    // Search filter
    const searchMatch = searchQuery === '' || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (post.content && post.content.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return categoryMatch && searchMatch;
  });

const handleNewsletterSubmit = async (e) => {
  e.preventDefault();
  setIsSubscribing(true);

  try {
    const response = await axios.post(`${API}/newsletter`, { email });

    console.log("Newsletter success:", response.status, response.data);

    toast.success("Successfully Subscribed!", {
      description: "You'll receive our latest insights and updates.",
    });
    setEmail("");
  } catch (error) {
    console.log("Newsletter error:", error?.response?.status, error?.response?.data, error);

    // If backend already saved it but returns an unusual response
    if (
      error?.response?.status === 409 ||
      error?.response?.status === 400
    ) {
      toast.success("Already Subscribed!", {
        description: "You're already on our mailing list.",
      });
      setEmail("");
    } else {
      toast.error("Subscription Failed", {
        description:
          error?.response?.data?.detail ||
          "Please try again later.",
      });
    }
  } finally {
    setIsSubscribing(false);
  }
};

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">Blog</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed mb-8">
            Insights, strategies, and trends in digital marketing and AI-powered growth
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles by title, topic, or keyword..."
                className="w-full px-6 py-4 pl-12 rounded-full bg-gray-900 border border-cyan-500/30 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 transition-colors duration-300"
              />
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Categories Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Explore by <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">Category</span>
          </h2>
          <div className="flex flex-wrap gap-4 justify-center">
            {allCategories.map((category) => (
              <Badge
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 text-base cursor-pointer transition-all duration-300 hover:scale-105 shadow-lg ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-cyan-500 to-purple-600 text-white border-cyan-400'
                    : 'bg-gradient-to-r from-gray-800 to-gray-900 hover:from-cyan-600 hover:to-purple-700 text-gray-300 hover:text-white border border-cyan-500/30 hover:border-cyan-400'
                }`}
              >
                {category}
              </Badge>
            ))}
          </div>
          <p className="text-center text-gray-400 mt-4">
            Showing {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''}
            {searchQuery && ` matching "${searchQuery}"`}
          </p>
        </div>

        {/* Featured Post */}
        {filteredPosts.length > 0 && (
          <Link to={`/blog/${filteredPosts[0].id}`}>
            <Card className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 rounded-3xl overflow-hidden hover:border-cyan-400 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/30 mb-16 cursor-pointer group">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="relative h-96 lg:h-auto overflow-hidden">
                  <img
                    src={filteredPosts[0].image}
                    alt={filteredPosts[0].title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent lg:hidden"></div>
                </div>
                <div className="p-10 flex flex-col justify-center">
                  <Badge className="bg-gradient-to-r from-cyan-500 to-purple-600 text-white border-0 w-fit mb-4">
                    Featured Post
                  </Badge>
                  <h2 className="text-4xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors duration-300">
                    {filteredPosts[0].title}
                  </h2>
                  <p className="text-gray-400 text-lg leading-relaxed mb-6">
                    {filteredPosts[0].excerpt}
                  </p>
                  <div className="flex items-center space-x-6 text-gray-500 text-sm mb-6">
                    <div className="flex items-center space-x-2">
                      <Calendar size={16} />
                      <span>{new Date(filteredPosts[0].date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <User size={16} />
                      <span>{filteredPosts[0].author}</span>
                    </div>
                  </div>
                  <Button className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white px-6 py-3 rounded-full font-semibold shadow-lg shadow-cyan-500/30 w-fit">
                    Read More <ArrowRight className="ml-2" size={16} />
                  </Button>
                </div>
              </div>
            </Card>
          </Link>
        )}

        {/* Blog Grid */}
        {filteredPosts.length > 1 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {filteredPosts.slice(1).map((post) => (
              <Link key={post.id} to={`/blog/${post.id}`}>
                <Card
                  className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/30 rounded-2xl overflow-hidden hover:border-purple-400 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/30 hover:scale-105 group cursor-pointer h-full"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div>
                    <Badge className="absolute top-4 left-4 bg-gradient-to-r from-purple-500 to-pink-600 text-white border-0">
                      {post.category}
                    </Badge>
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-purple-400 transition-colors duration-300">
                      {post.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed mb-4">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center space-x-4 text-gray-500 text-sm mb-4">
                      <div className="flex items-center space-x-2">
                        <Calendar size={14} />
                        <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <User size={14} />
                        <span>{post.author}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      className="text-purple-400 hover:text-purple-300 p-0 h-auto font-semibold"
                    >
                      Read More <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-2xl text-gray-400 mb-4">No articles found in this category</p>
            <Button
              onClick={() => setSelectedCategory('All')}
              className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white px-6 py-3 rounded-full"
            >
              View All Articles
            </Button>
          </div>
        ) : null}

        {/* Newsletter Section */}
        <Card className="bg-gradient-to-r from-cyan-900/40 to-purple-900/40 border border-cyan-500/50 p-12 rounded-3xl backdrop-blur-sm shadow-2xl shadow-cyan-500/30 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Stay Updated with Latest Insights
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter for exclusive tips, strategies, and industry trends
          </p>
          <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto flex flex-col sm:flex-row gap-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              disabled={isSubscribing}
              className="flex-1 px-6 py-4 rounded-full bg-black/50 border border-cyan-500/30 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 transition-colors duration-300 disabled:opacity-50"
            />
            <Button 
              type="submit"
              disabled={isSubscribing}
              className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white px-8 py-4 rounded-full font-semibold shadow-lg shadow-cyan-500/50 hover:shadow-cyan-500/70 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubscribing ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Blog;
